import { MenuItem } from "@mui/material";
import React from "react";
import {
	CustomChips,
	CustomIconButton,
	CustomSelect,
	CustomSwitch,
	CustomTable,
	CustomTableBody,
	CustomTableCell,
	CustomTableHead,
	CustomTableRow,
	CustomTooltip,
	icon,
	UserAvatar,
} from "../../../../common/components";
import { translate, translateEnumProjectRoleDescription } from "../../../../common/providers";
import styles from "./UsersListTable.module.css";

function getUserStatus(user) {
	if (user.locked) {
		return "inactive";
	}
	if (Array.isArray(user.projects) && user.projects.length === 0) {
		return "no-project";
	}
	if (!user.registered) {
		return "pending";
	}
	return "active";
}

const STATUS_CHIP_CONFIG = {
	active: { label: "admin.user-list.status.active", color: "success", startIcon: icon.faCheckCircle },
	pending: { label: "admin.user-list.status.pending", color: "warning", startIcon: icon.faClock },
	"no-project": {
		label: "admin.user-list.status.no-project",
		color: "warning",
		startIcon: icon.faTriangleExclamation,
	},
	inactive: { label: "admin.user-list.status.inactive", color: "error", startIcon: icon.faTimesCircle },
};

export default function UsersListTable({ companyId, loggedUser, users, onChangeLock, onChangeRole, COMPANY_ROLES }) {
	const tableHeaders = [
		translate("admin.user-list.th.name"),
		translate("admin.user-list.th.role"),
		translate("admin.user-list.th.status"),
		translate("admin.user-list.th.access"),
	];
	return (
		<CustomTable stickyHeader>
			<CustomTableHead>
				<CustomTableRow>
					{tableHeaders.map((header) => (
						<CustomTableCell key={header} className={styles.headerCell}>
							{header}
						</CustomTableCell>
					))}
				</CustomTableRow>
			</CustomTableHead>
			<CustomTableBody emptyState={translate("admin.user-list.empty-state.user")}>
				{Array.isArray(users) &&
					users.map((user) => {
						const status = getUserStatus(user);
						const isLoggedUser = user.id === loggedUser.id;
						const { label, color, startIcon } = STATUS_CHIP_CONFIG[status] || STATUS_CHIP_CONFIG.active;
						const roleDesc = user.companyRole && translateEnumProjectRoleDescription(user.companyRole);
						const roleTooltip =
							roleDesc && !roleDesc.startsWith("common:") ? (
								<CustomTooltip title={roleDesc}>
									<span>
										<CustomIconButton icon={icon.faCircleInfo} size="sm" variant="text" />
									</span>
								</CustomTooltip>
							) : null;
						return (
							<CustomTableRow
								key={user.id}
								hover
								className={`${styles.tableRow} ${user.locked ? styles["tableRow--disabled"] : ""}`}
							>
								<CustomTableCell className={styles.cell__name}>
									<div className={styles.cell__nameContent}>
										<UserAvatar
											companyId={companyId}
											displayName={user.displayName}
											email={user.email}
											firstName={user.firstName}
											lastName={user.lastName}
											size="sm"
											userId={user.id}
										/>
										<div className={styles.cell__nameText}>
											<span className={styles["cell__name--bold"]} translate="no">
												{user.displayName}
											</span>
											<span className={styles["cell__name--email"]} translate="no">
												{user.email}
											</span>
										</div>
									</div>
								</CustomTableCell>
								<CustomTableCell>
									<div className={styles.roleCell}>
										<CustomSelect
											className={styles.roleSelect}
											disabled={isLoggedUser || user.locked}
											size="sm"
											value={user.companyRole || ""}
											onChange={(e) => onChangeRole({ role: e.target.value, user })}
										>
											{Array.isArray(COMPANY_ROLES) &&
												COMPANY_ROLES.map((role) => (
													<MenuItem key={role.name} value={role.name}>
														{translate(`admin.user-list.role.${role.name}`)}
													</MenuItem>
												))}
										</CustomSelect>
										{roleTooltip}
									</div>
								</CustomTableCell>
								<CustomTableCell>
									<CustomChips
										color={color}
										label={translate(label)}
										size="sm"
										startIcon={startIcon}
									/>
								</CustomTableCell>
								<CustomTableCell align="right">
									{isLoggedUser ? (
										"-"
									) : (
										<CustomSwitch
											checked={!user.locked}
											color="primary"
											size="small"
											onChange={() => onChangeLock({ user })}
										/>
									)}
								</CustomTableCell>
							</CustomTableRow>
						);
					})}
			</CustomTableBody>
		</CustomTable>
	);
}
